import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { CalendarClock, Presentation, Loader2 } from 'lucide-react';
import { listDemoEnvironments, runDemoEnvironment } from '../../services/api';
import type { DemoEnvironmentResponse } from '../../services/api/types.gen';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import {
  DEMO_ENVIRONMENT_STATUS_PRESENTATION,
  compareByUrgency,
  refreshToggleHint,
} from './demoEnvironmentStatus';
import { inferDemoAgencySetupStageLabel } from './demoAgencySetupUi';
import { AgencyRail } from './AgencyRail';
import { DemoAgencyPane } from './DemoAgencyPane';
import { demoLastWorkedOn, demoRailRows, matchesSearch } from './demoRailRows';
import { recentAgencies } from './agencyRecents';
import { formatDateInPT } from '../../utils/dateUtils';
import { formatPullSummary, usePullKinnser } from './usePullKinnser';

/** Only the demo environment runs the nightly job; elsewhere the badge is
 *  dim and says so rather than promising a refresh nothing performs. */

/** How often the list refetches while a run is in flight. */
const RUN_POLL_INTERVAL_MS = 3000;

/** Which agencies the rail lists: the ones worked on lately, or every one. */
type Filter = 'recent' | 'all';

function formatMoment(value: string | null | undefined): string {
  if (!value) return '—';
  return formatDateInPT(value);
}

function displayName(environment: DemoEnvironmentResponse): string {
  return environment.agency_written_name || environment.agency_system_name;
}

function isRunning(environment: DemoEnvironmentResponse): boolean {
  return environment.latest_run?.status === 'running';
}

function listGroup(environment: DemoEnvironmentResponse): number {
  return environment.has_config ? 0 : 1;
}

export const DemoEnvironmentsList: React.FC = () => {
  // Only demo has the nightly refresh schedule; elsewhere the toggle is
  // disabled rather than promising something nothing keeps.
  const [environments, setEnvironments] = useState<DemoEnvironmentResponse[]>(
    []
  );
  // Only the first load blanks the screen; polling must not collapse the
  // expanded rows.
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  // Chosen after the first load: until then nobody knows whether there is
  // anything in "My sandboxes" to show.
  const [filter, setFilter] = useState<Filter | null>(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingRun, setPendingRun] = useState<DemoEnvironmentResponse | null>(
    null
  );

  // Polls do not wait for each other, so a slow response can land after a
  // newer one. An answer older than one already applied is dropped. Comparing
  // with the newest request instead would drop every answer once each takes
  // longer than the poll interval.
  const latestLoad = useRef(0);
  const appliedLoad = useRef(0);

  const load = useCallback(async () => {
    const thisLoad = ++latestLoad.current;
    try {
      const res = await listDemoEnvironments({ throwOnError: true });
      if (thisLoad < appliedLoad.current) return;
      appliedLoad.current = thisLoad;
      const rows = [...(res.data?.environments ?? [])];
      setEnvironments(rows);
      // Land on "All agencies" when nothing has been touched yet, so the first
      // visit is not an empty list with no obvious next step.
      setLoadFailed(false);
    } catch (error) {
      if (thisLoad < appliedLoad.current) return;
      console.error('Failed to load demo sandboxes', error);
      setLoadFailed(true);
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll only while something is running; at rest the list does not refetch.
  const anyRunning = environments.some(isRunning);
  useEffect(() => {
    if (!anyRunning) return;
    const timer = window.setInterval(() => void load(), RUN_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [anyRunning, load]);

  const allRows = useMemo(() => demoRailRows(environments), [environments]);
  // The window is worked out from the rows, so it widens on a quiet month and
  // tightens on a busy week without anybody setting it.
  const recents = useMemo(
    () => recentAgencies(allRows, new Date(), demoLastWorkedOn),
    [allRows]
  );
  // Recent by default, but only once there is something in it: otherwise the
  // first visit is an empty list with no obvious next step.
  const activeFilter: Filter =
    filter ?? (recents.agencyIds.size > 0 ? 'recent' : 'all');

  // Filtered over the rows the rail draws, so the search rule is the one in
  // demoRailRows rather than a second copy of it here.
  const railRows = useMemo(
    () =>
      allRows
        .filter(
          row => activeFilter === 'all' || recents.agencyIds.has(row.agencyId)
        )
        .filter(row => matchesSearch(row, search))
        .sort((a, b) => {
          // Built ones first, then untouched. Without grouping, hundreds of
          // untouched "Never run" rows would sort above the sandboxes somebody
          // is actually using, because Never run outranks Idle and Active.
          const group = listGroup(a.environment) - listGroup(b.environment);
          if (group !== 0) return group;
          const urgency = compareByUrgency(
            a.environment.status,
            b.environment.status
          );
          if (urgency !== 0) return urgency;
          return a.displayName.localeCompare(b.displayName);
        }),
    [allRows, activeFilter, search, recents]
  );
  const visible = useMemo(
    () => railRows.map(row => row.environment),
    [railRows]
  );

  /**
   * The agency the pane shows.
   *
   * The first visible one until somebody picks another, and back to the first
   * if the one they picked drops out of the filter or the search -- a pane
   * showing an agency the rail no longer lists is worse than one showing the
   * top of it.
   */
  const selected =
    visible.find(row => row.agency_id === selectedId) ?? visible[0] ?? null;

  if (initialLoading) {
    return (
      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading demo sandboxes…
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className='space-y-3'>
        <div className='flex items-start justify-between gap-4'>
          <p className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Presentation className='h-4 w-4 shrink-0' />
            Manage demo sandboxes for Kinnser-synced agencies. Non-prod only.
          </p>
        </div>

        {loadFailed && (
          <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
            <p className='text-sm text-red-700'>
              Unable to load demo sandboxes. Please try again.
            </p>
            <Button size='sm' variant='outline' onClick={() => void load()}>
              Retry
            </Button>
          </div>
        )}

        {/* The list is a box; the agency beside it is the page. Two vertical
            scrollers, one inside the other, meant the page scrolled and then
            the content scrolled again inside it -- and the inner one is never
            the one your wheel is over. So the list sticks and scrolls on its
            own, and the agency's content just runs down the page. */}
        <div className='flex flex-col gap-4 md:flex-row md:items-start'>
          <div className='flex max-h-[26rem] flex-col overflow-hidden rounded-md border md:sticky md:top-4 md:max-h-[calc(100vh-7rem)] md:w-[17rem] md:shrink-0'>
            {/* Above the list, not across the page: these narrow the list and
                nothing else, and spanning the whole width left them floating
                over content they have no bearing on. */}
            <div className='flex shrink-0 flex-col gap-2 border-b bg-muted/40 p-2'>
              <Input
                inputSize='sm'
                placeholder='Search agencies…'
                aria-label='Search agencies'
                value={search}
                onChange={event => setSearch(event.target.value)}
              />
              {/* Recent is worked out from the rows rather than curated: an
                  agency is here because somebody pulled or built it, which is
                  the same evidence a favourite stood in for, and it drops off
                  by itself. */}
              <div
                role='group'
                aria-label='Which agencies to show'
                className='flex rounded-md border bg-background p-[3px]'
              >
                <Button
                  type='button'
                  size='sm'
                  className='flex-1'
                  variant={activeFilter === 'recent' ? 'secondary' : 'ghost'}
                  aria-pressed={activeFilter === 'recent'}
                  onClick={() => setFilter('recent')}
                  title='Pulled or built lately. The window grows until it holds ten, up to sixteen weeks.'
                >
                  Recent ({recents.agencyIds.size})
                </Button>
                <Button
                  type='button'
                  size='sm'
                  className='flex-1'
                  variant={activeFilter === 'all' ? 'secondary' : 'ghost'}
                  aria-pressed={activeFilter === 'all'}
                  onClick={() => setFilter('all')}
                >
                  All ({environments.length})
                </Button>
              </div>
            </div>
            {visible.length === 0 ? (
              <p className='p-3 text-sm text-muted-foreground'>
                {search.trim()
                  ? 'No agencies match that search.'
                  : activeFilter === 'recent'
                    ? 'No sandboxes yet. Pull or run an agency under All agencies to add it here.'
                    : 'No Kinnser agencies found.'}
              </p>
            ) : (
              <AgencyRail
                rows={railRows}
                selectedId={selected?.agency_id ?? null}
                onSelect={setSelectedId}
                badge={row => (
                  <DemoRefreshBadge environment={row.environment} />
                )}
                state={row => <DemoRailState environment={row.environment} />}
              />
            )}
          </div>

          <div className='min-w-0 flex-1'>
            <DemoAgencyPane
              row={selected}
              agencies={environments}
              busy={selected ? isRunning(selected) : false}
              onRun={setPendingRun}
              onSaved={() => void load()}
              onPulled={() => void load()}
            />
          </div>
        </div>

        <DemoEnvironmentRunConfirmDialog
          environment={pendingRun}
          onClose={() => setPendingRun(null)}
          onStarted={agencyId => {
            setPendingRun(null);
            // Show the agency so the operator sees the run start.
            setSelectedId(agencyId);
            void load();
          }}
          onAlreadyRunning={() => void load()}
          onPulled={() => void load()}
        />
      </div>
    </TooltipProvider>
  );
};

/** The latest run in at most two lines; the steps are in the expanded row. */
/**
 * The one line under the name: what this sandbox is, and when it was built.
 *
 * The status word carries the urgency, so the date beside it is the fact a
 * label cannot hold. Last run and the nightly toggle are in the pane, where
 * the agency they describe already is.
 */
/**
 * Whether the nightly job keeps this sandbox current, before you pick it.
 *
 * The same badge slot and the same clock Agency Data Fill puts its daily sync
 * in, because it answers the same question about a row: is anything keeping
 * this up to date, or is what you are looking at as old as the last build.
 */
const DemoRefreshBadge: React.FC<{ environment: DemoEnvironmentResponse }> = ({
  environment,
}) => {
  // Per render, like every other reader of the stage: pinned at module load
  // it answered before a test could set the stage, so the badge could never
  // be shown on.
  const scheduled = inferDemoAgencySetupStageLabel() === 'demo';
  // Read once so the icon and its tooltip cannot disagree, and as a boolean:
  // the saved settings are optional on the wire.
  const nightly = environment.settings.refresh_nightly ?? false;
  const on = scheduled && nightly;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* The label rides on the span: lucide marks its svg aria-hidden. */}
        <span
          role='img'
          aria-label={`Nightly refresh ${on ? 'on' : 'off'} for ${displayName(environment)}`}
          className='inline-flex shrink-0'
        >
          <CalendarClock
            className={`h-4 w-4 ${on ? 'text-green-700' : 'text-muted-foreground/30'}`}
          />
        </span>
      </TooltipTrigger>
      <TooltipContent className='max-w-[260px]'>
        {refreshToggleHint({
          scheduled,
          on: nightly,
          setUp: environment.setup_at !== null,
          clickable: false,
        })}
      </TooltipContent>
    </Tooltip>
  );
};

const DemoRailState: React.FC<{ environment: DemoEnvironmentResponse }> = ({
  environment,
}) => {
  const presentation = DEMO_ENVIRONMENT_STATUS_PRESENTATION[environment.status];
  return (
    <>
      {presentation.label}
      {environment.setup_at && ` · built ${formatMoment(environment.setup_at)}`}
    </>
  );
};

// Confirm first because a run clears the agency's patients and clinics and
// rebuilds it;
// one click from a list is too easy for something that destructive. The dialog
// closes as soon as the run starts: the run happens on a worker, and its
// progress shows in the expanded row.
//
// A run builds only on data already pulled from Kinnser, so for an agency with
// no clinics the dialog offers the pull first and Run once it has succeeded —
// first setup stays in one dialog rather than a refusal and a trip elsewhere.
const DemoEnvironmentRunConfirmDialog: React.FC<{
  environment: DemoEnvironmentResponse | null;
  onClose: () => void;
  onStarted: (agencyId: string) => void;
  onAlreadyRunning: () => void;
  onPulled: () => void;
}> = ({ environment, onClose, onStarted, onAlreadyRunning, onPulled }) => {
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    pull,
    pulling,
    result: pullResult,
    error: pullError,
    reset: resetPull,
  } = usePullKinnser();

  useEffect(() => {
    if (environment === null) {
      setError(null);
      resetPull();
    }
  }, [environment, resetPull]);

  // The environment is a snapshot taken when the dialog opened, so a pull made
  // here is read from its own result rather than a refreshed list row.
  const latestPull = pullResult ?? environment?.kinnser_pull ?? null;
  // A build no longer needs a pull: with no Kinnser clinics it invents one
  // named after the agency, which is the whole point of showing a demo to an
  // agency that is not on Kinnser. Run is always available; pulling is a
  // separate thing you may also want.
  const hasClinics = (latestPull?.clinics ?? 0) > 0;
  const busy = starting || pulling;

  const start = async () => {
    if (!environment) return;
    setStarting(true);
    setError(null);
    try {
      await runDemoEnvironment({
        path: { agency_id: environment.agency_id },
        throwOnError: true,
      });
      onStarted(environment.agency_id);
    } catch (caught) {
      console.error('Failed to start demo setup', caught);
      // Somebody else started it; refresh so the row shows that run.
      if (getHttpStatus(caught) === 409) onAlreadyRunning();
      setError(
        getBusinessErrorMessage(caught) ??
          'Unable to start the run. Please try again.'
      );
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog
      open={environment !== null}
      onOpenChange={open => {
        if (!open && !busy) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Run demo setup for {environment ? displayName(environment) : ''}?
          </DialogTitle>
          <DialogDescription>
            This deletes all of the agency’s patients, intakes and clinics, with
            their insurances, physicians and staff access, removes every member,
            and resets agency settings and custom roles to Roger’s defaults. It
            then builds it again from the saved settings and the Kinnser pull,
            and invites only the demo team in the settings. It runs in the
            background and takes several minutes; you can close this page.
          </DialogDescription>
        </DialogHeader>
        {latestPull && (
          <p className='text-sm text-muted-foreground'>
            {formatPullSummary(latestPull)}
          </p>
        )}
        {!hasClinics && (
          <p className='text-sm text-muted-foreground'>
            {latestPull
              ? 'The last pull found no clinics. The sandbox gets one invented clinic named after the agency; pull again to build on its own.'
              : 'Not pulled from Kinnser. The sandbox gets one invented clinic named after the agency.'}
          </p>
        )}
        {pullError && <p className='text-sm text-red-700'>{pullError}</p>}
        {error && <p className='text-sm text-red-700'>{error}</p>}
        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </Button>
          {!hasClinics && (
            <Button
              type='button'
              variant='outline'
              disabled={busy || !environment}
              onClick={async () => {
                if (!environment) return;
                if (await pull(environment.agency_id)) onPulled();
              }}
            >
              {pulling && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Pull from Kinnser
            </Button>
          )}
          <Button type='button' onClick={() => void start()} disabled={busy}>
            {starting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Run setup
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DemoEnvironmentsList;
